import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { TrendingDown, TrendingUp, Clock, Users, DollarSign, BarChart3, Award, FileDown, Loader2, Filter } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell
} from 'recharts';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const COLORS = ['#0F1B3D', '#B8845C', '#0F1B3D', '#A8174E', '#E9C46A', '#7A85A0'];

const formatMonth = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const months = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
  return `${months[parseInt(mo, 10) - 1]}/${y?.slice(2)}`;
};

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtBRDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('pt-BR');
};

const useFunnelPresets = () => useMemo(() => {
  const today = new Date();
  const todayISO = toISO(today);
  const firstOfMonth = toISO(new Date(today.getFullYear(), today.getMonth(), 1));
  const ago7 = new Date(today); ago7.setDate(today.getDate() - 6);
  const ago30 = new Date(today); ago30.setDate(today.getDate() - 29);
  const firstPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastPrev = new Date(today.getFullYear(), today.getMonth(), 0);
  const firstOfYear = new Date(today.getFullYear(), 0, 1);
  return {
    today:      { start: todayISO, end: todayISO, label: 'Hoje' },
    last7:      { start: toISO(ago7), end: todayISO, label: '7 dias' },
    last30:     { start: toISO(ago30), end: todayISO, label: '30 dias' },
    thisMonth:  { start: firstOfMonth, end: todayISO, label: 'Este mês' },
    lastMonth:  { start: toISO(firstPrev), end: toISO(lastPrev), label: 'Mês passado' },
    thisYear:   { start: toISO(firstOfYear), end: todayISO, label: 'Este ano' },
  };
}, []);

const ConversionFunnel = () => {
  const presets = useFunnelPresets();
  const [start, setStart] = useState(presets.thisMonth.start);
  const [end, setEnd] = useState(presets.thisMonth.end);
  const [applied, setApplied] = useState({ start: presets.thisMonth.start, end: presets.thisMonth.end });
  const [funnel, setFunnel] = useState(null);

  const fetchFunnel = useCallback(async (s, e) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/stats/funnel`, {
        params: { start_date: s, end_date: e },
        withCredentials: true,
      });
      setFunnel(data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchFunnel(applied.start, applied.end); }, [fetchFunnel, applied]);

  const apply = () => { if (start && end && start <= end) setApplied({ start, end }); };
  const clear = () => { setStart(presets.thisMonth.start); setEnd(presets.thisMonth.end); setApplied({ start: presets.thisMonth.start, end: presets.thisMonth.end }); };
  const setPreset = (p) => { setStart(p.start); setEnd(p.end); setApplied({ start: p.start, end: p.end }); };

  const steps = funnel?.steps || [];
  const maxCount = Math.max(1, ...steps.map(s => s.count));

  return (
    <Card className="border-[#E5DCC8]" data-testid="conversion-funnel">
      <CardHeader>
        <div className="flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-[#A8174E]" />
          <div>
            <CardTitle className="text-lg">Funil de Conversão</CardTitle>
            <CardDescription>Cadastrados → Logaram → Marcaram → Concluíram</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Period filter */}
        <div className="rounded-lg border border-[#E5DCC8] bg-[#FAF6EE]/40 p-4 space-y-3" data-testid="funnel-filter">
          <div className="flex flex-col md:flex-row md:items-end gap-3">
            <div className="flex items-center gap-2 mr-1 text-[#0F1B3D]">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">Período</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 flex-1">
              <div className="flex-1"><label className="text-xs text-[#7A85A0]">Data inicial</label><Input type="date" value={start} onChange={e => setStart(e.target.value)} data-testid="funnel-start-date" /></div>
              <div className="flex-1"><label className="text-xs text-[#7A85A0]">Data final</label><Input type="date" value={end} onChange={e => setEnd(e.target.value)} data-testid="funnel-end-date" /></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={apply} className="bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="funnel-apply-btn">Aplicar</Button>
              <Button onClick={clear} variant="outline" className="border-[#E5DCC8]" data-testid="funnel-clear-btn">Limpar</Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-[#FAF6EE]">
            {Object.entries(presets).map(([key, p]) => {
              const isActive = applied.start === p.start && applied.end === p.end;
              return (
                <button key={key} onClick={() => setPreset(p)} className={`px-3 py-1 text-xs rounded-full border transition-all ${isActive ? 'bg-[#0F1B3D] text-white border-[#0F1B3D]' : 'border-[#E5DCC8] text-[#5F6B85] hover:bg-[#FAF6EE]'}`} data-testid={`funnel-preset-${key}`}>{p.label}</button>
              );
            })}
          </div>
          <div className="text-sm text-[#5F6B85] pt-1">
            <span className="font-medium text-[#0F1B3D]">Período:</span>{' '}
            <span data-testid="funnel-period-display">{fmtBRDate(applied.start)} até {fmtBRDate(applied.end)}</span>
          </div>
        </div>

        {/* Funnel bars */}
        <div className="space-y-3">
          {steps.map((s, i) => {
            const width = Math.max(6, Math.round((s.count / maxCount) * 100));
            const colors = ['#0F1B3D', '#B8845C', '#A8174E', '#065f46'];
            return (
              <div key={s.key} data-testid={`funnel-step-${s.key}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-sm font-medium text-[#0F1B3D]">{i + 1}. {s.label}</span>
                  <span className="text-sm text-[#5F6B85]"><strong className="text-[#0F1B3D]">{s.count}</strong> · {s.pct_of_first}%</span>
                </div>
                <div className="h-8 rounded-lg bg-[#FAF6EE] overflow-hidden">
                  <div
                    className="h-full rounded-lg flex items-center justify-end px-3 text-xs text-white font-medium transition-all"
                    style={{ width: `${width}%`, background: colors[i] || '#7A85A0' }}
                  >
                    {s.count > 0 && s.count}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export const AnalyticsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    try {
      const { data: analytics } = await axios.get(`${API_URL}/api/analytics/dashboard`, { withCredentials: true });
      setData(analytics);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/insights/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `insights_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Relatório de Insights exportado!');
    } catch (error) {
      toast.error('Erro ao gerar relatório');
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0F1B3D]"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6" data-testid="analytics-dashboard">
        <ConversionFunnel />
        <Card className="border-[#E5DCC8]">
          <CardContent className="p-8 text-center">
            <p className="text-[#7A85A0]">Sem dados suficientes para gerar os demais gráficos de insights. Adicione pacientes, consultas e pagamentos para desbloquear.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { attendance, revenue, peak_hours, top_patients, weight_loss } = data;

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Insights & Analytics
          </h2>
          <p className="text-[#5F6B85]">Visão estratégica da sua clínica</p>
        </div>
        <Button
          variant="outline"
          className="border-[#E5DCC8] hover:bg-[#FAF6EE]"
          onClick={handleDownloadPdf}
          disabled={pdfLoading}
          data-testid="download-insights-pdf"
        >
          {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
          {pdfLoading ? 'Gerando...' : 'Exportar PDF'}
        </Button>
      </div>

      {/* Conversion Funnel with its own period filter */}
      <ConversionFunnel />

      {/* KPI Cards Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          icon={<BarChart3 className="h-5 w-5" />}
          label="Taxa Comparecimento"
          value={`${attendance.rate}%`}
          sub={`${attendance.completed} de ${attendance.total}`}
          color="#0F1B3D"
          testId="kpi-attendance"
        />
        <KpiCard
          icon={<DollarSign className="h-5 w-5" />}
          label="Receita Confirmada"
          value={`R$ ${revenue.total_paid.toFixed(0)}`}
          sub={`R$ ${revenue.total_pending.toFixed(0)} pendente`}
          color="#0F1B3D"
          testId="kpi-revenue"
        />
        <KpiCard
          icon={<Clock className="h-5 w-5" />}
          label="Horário de Pico"
          value={peak_hours.busiest_hour}
          sub="mais procurado"
          color="#B8845C"
          testId="kpi-peak"
        />
        <KpiCard
          icon={<TrendingDown className="h-5 w-5" />}
          label="Perda Média"
          value={`${weight_loss.avg_loss_kg} kg`}
          sub={`${weight_loss.avg_loss_pct}% do peso`}
          color="#0F1B3D"
          testId="kpi-weight"
        />
        <KpiCard
          icon={<Users className="h-5 w-5" />}
          label="Emagrecendo"
          value={`${weight_loss.patients_losing}/${weight_loss.total_tracked}`}
          sub="pacientes"
          color="#0F1B3D"
          testId="kpi-losing"
        />
      </div>

      {/* Charts Row 1: Attendance + Revenue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Chart */}
        <Card className="border-[#E5DCC8]" data-testid="attendance-chart">
          <CardHeader>
            <CardTitle className="text-lg">Taxa de Comparecimento Mensal</CardTitle>
            <CardDescription>Consultas concluídas vs canceladas</CardDescription>
          </CardHeader>
          <CardContent>
            {attendance.chart.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={attendance.chart.map(c => ({ ...c, month: formatMonth(c.month) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5DCC8" />
                  <XAxis dataKey="month" tick={{ fill: '#5F6B85', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#5F6B85', fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="completed" name="Concluídas" fill="#0F1B3D" radius={[4,4,0,0]} />
                  <Bar dataKey="cancelled" name="Canceladas" fill="#A8174E" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>

        {/* Revenue Chart */}
        <Card className="border-[#E5DCC8]" data-testid="revenue-chart">
          <CardHeader>
            <CardTitle className="text-lg">Receita Mensal</CardTitle>
            <CardDescription>Faturamento pago vs pendente</CardDescription>
          </CardHeader>
          <CardContent>
            {revenue.chart.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={revenue.chart.map(c => ({ ...c, month: formatMonth(c.month) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5DCC8" />
                  <XAxis dataKey="month" tick={{ fill: '#5F6B85', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#5F6B85', fontSize: 11 }} />
                  <Tooltip formatter={(val) => `R$ ${val.toFixed(2)}`} />
                  <Legend />
                  <Bar dataKey="paid" name="Pago" fill="#0F1B3D" radius={[4,4,0,0]} />
                  <Bar dataKey="pending" name="Pendente" fill="#E9C46A" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Peak Hours + Weight Loss */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Peak Hours */}
        <Card className="border-[#E5DCC8]" data-testid="peak-hours-chart">
          <CardHeader>
            <CardTitle className="text-lg">Horários de Pico</CardTitle>
            <CardDescription>Distribuição de consultas por horário</CardDescription>
          </CardHeader>
          <CardContent>
            {peak_hours.chart.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={peak_hours.chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5DCC8" />
                  <XAxis dataKey="hour" tick={{ fill: '#5F6B85', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#5F6B85', fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Consultas" fill="#B8845C" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>

        {/* Weight Loss Summary */}
        <Card className="border-[#E5DCC8]" data-testid="weight-loss-chart">
          <CardHeader>
            <CardTitle className="text-lg">Resultado do Tratamento (Mounjaro)</CardTitle>
            <CardDescription>Evolução de peso dos pacientes acompanhados</CardDescription>
          </CardHeader>
          <CardContent>
            {weight_loss.details.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={weight_loss.details} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5DCC8" />
                  <XAxis type="number" tick={{ fill: '#5F6B85', fontSize: 11 }} unit=" kg" />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fill: '#5F6B85', fontSize: 11 }} />
                  <Tooltip formatter={(val) => `${val} kg`} />
                  <Bar dataKey="change_kg" name="Variação (kg)">
                    {weight_loss.details.map((entry, i) => (
                      <Cell key={i} fill={entry.change_kg < 0 ? '#0F1B3D' : '#A8174E'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </CardContent>
        </Card>
      </div>

      {/* Top Patients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-[#E5DCC8]" data-testid="top-patients-visits">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Award className="h-5 w-5 text-[#B8845C]" />
              Top Pacientes por Consultas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_patients.by_visits.length > 0 ? (
              <div className="space-y-3">
                {top_patients.by_visits.map((p, i) => (
                  <div key={p.patient_id} className="flex items-center justify-between" data-testid={`top-visit-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${i === 0 ? 'bg-[#B8845C]' : i === 1 ? 'bg-[#7A85A0]' : 'bg-[#E5DCC8] text-[#5F6B85]'}`}>
                        {i + 1}
                      </span>
                      <span className="font-medium text-[#0F1B3D]">{p.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-[#0F1B3D]">{p.visits} consultas</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-[#7A85A0]">Sem dados</p>}
          </CardContent>
        </Card>

        <Card className="border-[#E5DCC8]" data-testid="top-patients-revenue">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="h-5 w-5 text-[#0F1B3D]" />
              Top Pacientes por Receita
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_patients.by_revenue.length > 0 ? (
              <div className="space-y-3">
                {top_patients.by_revenue.map((p, i) => (
                  <div key={p.patient_id} className="flex items-center justify-between" data-testid={`top-revenue-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${i === 0 ? 'bg-[#0F1B3D]' : i === 1 ? 'bg-[#7A85A0]' : 'bg-[#E5DCC8] text-[#5F6B85]'}`}>
                        {i + 1}
                      </span>
                      <span className="font-medium text-[#0F1B3D]">{p.name}</span>
                    </div>
                    <span className="text-sm font-semibold text-[#0F1B3D]">R$ {p.revenue.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-[#7A85A0]">Sem dados</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const KpiCard = ({ icon, label, value, sub, color, testId }) => (
  <Card className="border-[#E5DCC8]" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}15` }}>
          <div style={{ color }}>{icon}</div>
        </div>
        <div className="min-w-0">
          <p className="text-xs text-[#7A85A0] truncate">{label}</p>
          <p className="text-xl font-bold" style={{ color }}>{value}</p>
          <p className="text-xs text-[#7A85A0]">{sub}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const EmptyChart = () => (
  <div className="flex items-center justify-center h-[260px]">
    <p className="text-sm text-[#7A85A0]">Dados insuficientes para gráfico. Adicione mais registros.</p>
  </div>
);
